import { SAMPLE_RATE, SAMPLE_SETS, SAMPLE_SOUNDS, type SampleSetId } from "../assets/sounds.generated.ts";
import { loudestWindowDb } from "../audio/audio-model.ts";
import { audio, type SoundSource } from "../audio/engine.ts";
import { energyOnset } from "../audio/sample-bank.ts";
import { SOUND_IDS, type SoundId } from "../audio/recipes.ts";
import { mountAmbienceBench } from "./ambience-bench.ts";

export interface SoundViewer {
  stats(): Record<string, unknown>;
}

/** Nível de um som: a média, entre as variações, da janela RMS de 50 ms mais alta (dBFS), com o ganho de tocar. */
export interface SoundLevel {
  amostra?: number;
  sintese?: number;
  variacoes: number;
}

/** Variações decodificadas de um conjunto: tamanho e início (`energyOnset`), medidos e gravados no módulo. */
export interface SetCheck {
  frames: number[];
  expected: number[];
  onsetMs: number[];
  expectedOnsetMs: number[];
}

const ms = (frames: number, rate: number) => Math.round((frames / rate) * 10_000) / 10;

const dbText = (db: number | undefined) => (db === undefined || !Number.isFinite(db) ? "—" : `${db.toFixed(1).replace(".", ",")} dB`);

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

function level(buffers: readonly AudioBuffer[], gain = 1): number | undefined {
  if (!buffers.length) return undefined;
  const sum = buffers.reduce((n, b) => n + loudestWindowDb(b.getChannelData(0), b.sampleRate), 0);
  return Math.round((sum / buffers.length + 20 * Math.log10(gain)) * 10) / 10;
}

/**
 * Bancada de escuta (`?debug=sons`, sem servidor nem WebGL): os leitos de ambiente (F46, com o
 * simulador de contexto: `ambience-bench.ts`); cada som que tem amostra (F45) ao lado da receita
 * sintetizada que ela substitui, com o nível medido das duas, para o dono aprovar o tom e o
 * volume; embaixo, os sons que continuam só sintetizados. O primeiro clique cria o contexto de
 * áudio (política de autoplay). Nada sai da página: só os arquivos de `public/sons`.
 */
export function startSoundViewer(root: HTMLElement): SoundViewer {
  const sampled = SOUND_IDS.filter((id) => SAMPLE_SOUNDS[id]);
  const synthOnly = SOUND_IDS.filter((id) => !SAMPLE_SOUNDS[id]);
  const levels: Partial<Record<SoundId, SoundLevel>> = {};
  const sets: Partial<Record<SampleSetId, SetCheck>> = {};
  let ready = false;

  const panel = el("section", "panel sons");
  panel.append(el("h2", undefined, "Bancada de sons"));
  const status = el("p", "estado", "Afinando os sons…");
  panel.append(status);
  const bench = mountAmbienceBench(panel);
  panel.append(el("h3", undefined, "Efeitos"));
  panel.append(el("p", "nota", "Cada som com amostra gravada ao lado da síntese que ela substitui. Nível: a janela RMS de 50 ms mais alta, na média das variações, com o ganho de tocar (sem o passa-baixa dos passos de grama e lama). A amostra toca com ±4 % de tom; a síntese, com ±8 %."));

  const play = (id: SoundId, source: SoundSource, variation?: number, delay = 0) => {
    audio.unlock();
    audio.play(id, { source, bus: "efeitos", ...(variation !== undefined && { variation }), delay });
  };
  const button = (text: string, onClick: () => void, title?: string) => {
    const b = el("button", "small", text);
    b.type = "button";
    if (title) b.title = title;
    b.addEventListener("click", onClick);
    return b;
  };

  const table = el("table");
  const head = el("tr");
  for (const h of ["Som", "Amostra", "Síntese", "Nível (amostra · síntese)"]) head.append(el("th", undefined, h));
  table.append(el("thead"));
  table.tHead!.append(head);
  const body = el("tbody");
  const cells = new Map<SoundId, { amostra: HTMLElement; nivel: HTMLElement }>();
  for (const id of sampled) {
    const shape = SAMPLE_SOUNDS[id]!;
    const row = el("tr");
    const name = el("td");
    name.append(el("strong", undefined, id));
    const extra = [shape.set !== id && `toca ${shape.set}`, shape.rate !== 1 && `tom ${String(shape.rate).replace(".", ",")}`, shape.lowpass && `passa-baixa ${shape.lowpass} Hz`, shape.gain !== 1 && `ganho ${dbText(20 * Math.log10(shape.gain))}`].filter(Boolean);
    if (extra.length) name.append(el("small", undefined, extra.join(" · ")));
    const amostra = el("td", "botoes");
    amostra.append(el("span", "nota", "…"));
    const sintese = el("td", "botoes");
    sintese.append(button("Síntese", () => play(id, "sintese")));
    const nivel = el("td", "nivel", "—");
    row.append(name, amostra, sintese, nivel);
    body.append(row);
    cells.set(id, { amostra, nivel });
  }
  table.append(body);
  panel.append(table);

  const rest = el("details");
  rest.append(el("summary", undefined, `Só síntese (${synthOnly.length})`));
  const grid = el("div", "sintese-so");
  for (const id of synthOnly) grid.append(button(id, () => play(id, "sintese")));
  rest.append(grid);
  panel.append(rest);
  root.replaceChildren(panel);

  void audio.prerender().then(() => {
    const s = audio.stats().samples;
    for (const id of SOUND_IDS) {
      const synth = audio.variations(id, "sintese");
      const samples = audio.variations(id, "amostra");
      const shape = audio.sampleShape(id);
      const amostra = level(samples, shape?.gain ?? 1);
      levels[id] = { ...(amostra !== undefined && { amostra }), ...(synth.length > 0 && { sintese: level(synth)! }), variacoes: samples.length };
      const c = cells.get(id);
      if (!c) continue;
      c.nivel.textContent = `${dbText(amostra)} · ${dbText(levels[id]!.sintese)}`;
      if (!samples.length) {
        c.amostra.replaceChildren(el("span", "nota", "sem amostra (síntese)"));
        continue;
      }
      const longest = Math.max(...samples.map((b) => b.duration / (shape?.rate ?? 1)));
      c.amostra.replaceChildren(
        button("Amostra", () => play(id, "amostra"), "Uma variação sorteada, sem repetir a anterior"),
        button(`Todas (${samples.length})`, () => samples.forEach((_, v) => play(id, "amostra", v, v * (longest + 0.2))), "Cada variação, uma depois da outra"),
      );
    }
    // Cada conjunto pelo som de mesmo nome (os apelidos tocam as mesmas variações).
    for (const set of Object.keys(SAMPLE_SETS) as SampleSetId[]) {
      const buffers = audio.variations(set, "amostra");
      sets[set] = {
        frames: buffers.map((b) => b.length),
        expected: SAMPLE_SETS[set].map((f) => f.frames),
        onsetMs: buffers.map((b) => ms(energyOnset(b.getChannelData(0)), b.sampleRate)),
        expectedOnsetMs: SAMPLE_SETS[set].map((f) => ms(f.onset, SAMPLE_RATE)),
      };
    }
    status.textContent = s.decoded ? `${s.decoded} de ${s.expected} amostras (${s.format.toUpperCase()}) em ${s.ms} ms; decodificação ${s.decodeMs} ms; ${(s.pcmBytes / 1048576).toFixed(1).replace(".", ",")} MB de PCM.` : `Amostras indisponíveis (${s.error ?? "sem WebAudio"}): tudo sintetizado.`;
    ready = true;
  });

  return { stats: () => ({ ready, levels, sets, samples: audio.stats().samples, ...bench.stats() }) };
}
